import React, { createRef, RefObject, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { classNames } from "src/common/classNames";
import { applyOrderStd, TableStore } from "src/components/tables/TableStore";
import {
  PropsATVDynamic,
  TableSelectionCheckbox,
} from "src/pages/EntityFiltersPage/AsyncTableView";
import { manualRowSelection } from "src/components/tables/AsyncTable/manualRowSelection";
import { ColumnPoor, visibleFixedLeft } from "src/components/tables/ColumnPoor";
import styles from "./ATVRowStd.module.less";
import { drawColumn } from "../drawColumn";
import { TableSelectionRadio } from "../../tableSelection/TableSelectionRadio";
import { getColumnWidthStyles } from "../getColumnWidths";
import { ATVFreezeLine } from "../ATVFreezeLine/ATVFreezeLine";

/* eslint jsx-a11y/click-events-have-key-events: "off" */
/* eslint jsx-a11y/no-static-element-interactions: "off" */
/* eslint jsx-a11y/interactive-supports-focus: "off" */

export type PropsATVRowStd<TRow extends {}, TFilters extends {}> = {
  store: TableStore<TRow, TFilters>;
  row: TRow;
  rowKey: keyof TRow;
  dynamicProps: PropsATVDynamic;
  useSelection?: boolean;
  onClick?: (row: TRow) => void;
};

export const ATVRowStd = observer(
  <TRow extends {}, TFilters extends {}>({
    store,
    useSelection,
    row,
    rowKey,
    onClick,
    dynamicProps: { scroll },
  }: PropsATVRowStd<TRow, TFilters>) => {
    const keyValue = row[rowKey];
    const { selectionType, onSelect } = store.selectionSettings;
    const columnRefs = useMemo(
      () =>
        store.finalColumns.reduce(
          (acc, { key }) => ({ ...acc, [key]: createRef<HTMLDivElement>() }),
          {} as Record<string, RefObject<HTMLDivElement>>,
        ),
      [store.columns],
    );

    const onSelectionCellClick = () => {
      const checked = manualRowSelection(store, row);
      onSelect?.(store.selected, [row], checked);
    };

    const selected = !!store.selected.find(
      (value) => value[store.rowKey] === row[rowKey],
    );

    const isColumnSorted = (column: ColumnPoor) =>
      store.sort === column.key && !!store.sortOrder;

    const cells = [
      {
        key: "~selection",
        content: useSelection && (
          <div
            role="button"
            className={classNames([
              styles.rowItem,
              styles.selectionRow,
              [selected, styles.rowItemSelected],
            ])}
            onClick={(e) => {
              e.stopPropagation();
              onSelectionCellClick();
            }}
          >
            {selectionType === "checkbox" ? (
              <TableSelectionCheckbox store={store} row={row} />
            ) : (
              <TableSelectionRadio store={store} row={row} />
            )}
          </div>
        ),
      },
      ...store.finalColumns.map((column, colIndex) => ({
        key: column.key,
        content: (
          <div
            ref={columnRefs[column.key]}
            style={getColumnWidthStyles(store.columnWidths, column.key)}
            key={column.key}
            className={classNames([
              styles.rowItem,
              [isColumnSorted(column), styles.rowItemSorted],
              [selected, styles.rowItemSelected],
            ])}
          >
            {drawColumn(row, column, colIndex)}
          </div>
        ),
      })),
    ];
    const fixedCount = visibleFixedLeft(
      applyOrderStd(store, store.columns),
      store,
    );

    return (
      <ATVFreezeLine
        className={classNames([
          styles.tableRow,
          [selected, styles.tableRowSelected],
          [onClick, styles.tableRowClickable],
        ])}
        fixedLeftCount={fixedCount > 0 ? fixedCount + 1 : 0}
        cells={cells}
        key={String(keyValue)}
        onClick={() => {
          onClick?.(row);
        }}
        inScroll={scroll.x > 1}
      />
    );
  },
);
